import React, { useEffect, useRef, useCallback } from "react";
import { SearchInput } from "../../Components";
import NotSubscribe from "../../Components/Base/Common/NotSubscribe";
import { storageHandler } from "../../Helpers/storage";
import ReportItem from "./ReportItem";
import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
} from "@mui/material";
import { Plus, Search, Loader2 } from "lucide-react";

// Shimmer row component for loading state
const TableRowShimmer = ({ index }) => (
  <TableRow
    sx={{
      "& td": {
        py: 1.5,
        px: 2.5,
        borderBottom: "1px solid #F3F4F6",
      },
      backgroundColor:
        index % 2 === 0 ? "rgba(248, 250, 252, 0.5)" : "transparent",
    }}
  >
    <TableCell sx={{ minWidth: "200px" }}>
      <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "120px" }}>
      <div className="h-4 w-12 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "150px" }}>
      <div className="h-4 w-24 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "100px" }}>
      <div className="h-4 w-16 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "100px" }}>
      <div className="h-6 w-16 mx-auto rounded-md bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "100px" }}>
      <div className="h-6 w-16 mx-auto rounded-md bg-gray-200 animate-pulse" />
    </TableCell>
  </TableRow>
);

// const selectDescList = [
//   { id: "title", value: "Title" },
//   { id: "status", value: "Status" },
// ];

export default function ReportMain({
  setTab,
  reportData,
  disableSearchBox,
  paginationData,
  onPaginate,
  searchString,
  search,
  setSearchStringText,
  changeMediumDesc,
  editReport,
  openViewReport,
  onHeaderTitleChange,
  cartItem,
  closeCartPanel,
  setAuth,
  NotifModul,
  profiles,
  user,
  setModalNotification,
  goToPricing,
  setModal,
  isLoading,
  isLoadingMore,
  hasMore,
  loadMore,
  selectedTab,
  setSelectedTab,
  reachedEnd
}) {
  const tableContainerRef = useRef(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    
    if (isLoadingRef.current) return;
    if (!hasMore) return;

    if (scrollTop + clientHeight >= scrollHeight - 150) {
      if (loadMore) {
        loadMore();
      }
    }
  }, [hasMore, loadMore]);

  let data = null;
  try {
    const userInfo = storageHandler.getUserInfo();
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      data = parsed?.data || null;
    }
  } catch (error) {
    console.error("Error parsing user info:", error);
  }

  useEffect(() => {
    if (data && data.p_f_ids) {
      data.p_f_ids.includes(2);
    }
  }, [data]);

  // const selectedFilter =
  //   selectDescList.find((item) => item.id === (paginationData?.selectDesc || "title")) || selectDescList[0];

  return (
    <>
      {data?.p_f_ids?.includes(2) ? (
        <div
          className="dashboard"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 100px)",
            overflow: "hidden",
          }}
        >
          {/* Header Section */}
          <header
            className="flex pb-4 flex-col md:flex-row md:items-center md:justify-between gap-3"
            style={{ flexShrink: 0, zIndex: 20, position: "relative" }}
          >
            {/* Tab Filters */}
            <ul className="flex flex-wrap items-center gap-4 m-0 p-0 list-none">
              {[
                { id: "all", label: "All" },
                { id: "archive", label: "Archived" },
                { id: "nonarchive", label: "Active" },
              ].map((tab) => (
                <li key={tab.id}>
                  <button
                    onClick={() => setSelectedTab(tab.id)}
                    className={`flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200  ${selectedTab === tab.id
                      ? "bg-green-50 text-green-700 font-semibold shadow-sm border border-green-200"
                      : "bg-gray-50 text-gray-500 hover:bg-green-50 hover:text-green-700 border-none"
                      }`}
                    style={{
                      boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                    }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-3">
              {/* Filter Select */}
              {/* <div
                style={{
                  width: "120px",
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                  borderRadius: "9999px",
                }}
              >
                <AppSelectInput
                  list={selectDescList}
                  selected={selectedFilter}
                  onChange={changeMediumDesc}
                  className="!rounded-full !border-none !bg-gray-50"
                  style={{
                    paddingBottom: "0px",
                  }}
                />
              </div> */}

              {/* Search Input */}
              <div
                style={{
                  width: "280px",
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                  borderRadius: "9999px",
                }}
              >
                <SearchInput
                  type="text"
                  value={searchString}
                  onClick={search}
                  onChange={setSearchStringText}
                  icon={<Search color="#9CA3AF" strokeWidth={2.2} size={18} />}
                  placeholder="Search reports..."
                  className="!rounded-full !border-none !bg-gray-50 pb-0"
                  style={{
                    paddingBottom: "0px",
                  }}
                />
              </div>

              <button
                className="flex h-[38px] px-4 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90"
                style={{
                  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  boxShadow: "0px 2px 6px 0px rgba(16, 185, 129, 0.25)",
                }}
                onClick={() => {
                  setTab("create");
                  onHeaderTitleChange && onHeaderTitleChange("Create Report");
                }}
              >
                <Plus strokeWidth={2.2} size={18} />
                <span>Add Report</span>
              </button>
            </div>
          </header>

          {/* Table Container */}
          <div
            className="rounded-xl bg-white overflow-hidden w-full"
            style={{
              boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
              border: "1px solid rgba(16, 185, 129, 0.1)",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <div
              ref={tableContainerRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#e5e7eb transparent",
              }}
            >
              <Table
                sx={{
                  width: "100%",
                  minWidth: 800,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                    "& th": {
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      borderBottom: "2px solid #E2E8F0",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ py: 1.5, px: 2, minWidth: "200px" }}>Title</TableCell>
                    <TableCell sx={{ py: 1.5, px: 2, textAlign: "center", minWidth: "120px" }}># of Influencers</TableCell>
                    <TableCell sx={{ py: 1.5, px: 2, textAlign: "center", minWidth: "150px" }}>Timeframe</TableCell>
                    <TableCell sx={{ py: 1.5, px: 2, textAlign: "center", minWidth: "100px" }}>Token Used</TableCell>
                    <TableCell sx={{ py: 1.5, px: 2, textAlign: "center", minWidth: "100px" }}>Status</TableCell>
                    <TableCell sx={{ py: 1.5, px: 2, textAlign: "center", minWidth: "100px" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && reportData.length === 0 ? (
                    [...Array(8)].map((_, index) => (
                      <TableRowShimmer key={`shimmer-${index}`} index={index} />
                    ))
                  ) : reportData.length > 0 ? (
                    reportData.map((item, index) => (
                      <ReportItem
                        openViewReport={openViewReport}
                        editReport={editReport}
                        key={index}
                        item={item}
                        index={index}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{
                          py: 6,
                          color: "#6B7280",
                          fontSize: "14px",
                        }}
                      >
                        No reports found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Loading indicator for infinite scroll */}
              {isLoadingMore && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "16px",
                    color: "#6B7280",
                    fontSize: "14px",
                    gap: "8px",
                  }}
                >
                  <Loader2
                    size={20}
                    className="animate-spin"
                    style={{ color: "#10B981" }}
                  />
                  Loading more...
                </div>
              )}

              {/* Show "No more reports to load" when reached end and have data */}
              {reachedEnd && reportData.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "16px",
                    color: "#6B7280",
                    fontSize: "14px",
                    borderTop: "1px solid #E5E7EB",
                    marginTop: "8px",
                  }}
                >
                  No more reports to load
                </div>
              )}

              {/* Show "No reports found" when reached end and no data */}
              {reachedEnd && reportData.length === 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "32px",
                    color: "#6B7280",
                    fontSize: "14px",
                  }}
                >
                  No reports found
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <NotSubscribe
          goToPricing={goToPricing}
          type="brand"
          setAuth={setAuth}
          setModal={setModal}
          NotifModul={NotifModul}
          setNotifModul={setModalNotification}
          profiles={profiles}
          closeCartPanel={closeCartPanel}
          user={user}
          cartItem={cartItem}
          title={"Reports"}
          background={"background_reports.png"}
        />
      )}
    </>
  );
}
